import { useStoreState } from '@ariakit/react'
import { usePathname } from 'next/navigation'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import {
  DropzoneErrors,
  DropzoneProvider,
  DropzoneTextContainer,
  PreviewFile,
  TypeStyle,
  Upload,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useAuthentication } from '../../../../../../app/_providers/AuthenticationProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { FormBody } from '../../../../Modal/primitives/shared-form-styles'
import { ErrorMessage } from '../../../../Modal/primitives/shared-form-styles'
import { PriceLabel } from '../../../../Modal/primitives/shared-form-styles'
import * as Name from '../../TextEmbroideryForm/FormBody/Name'
import { LogoFormContainer } from '../LogoFormContainer'

import { LogoColorSelector } from './LogoColorSelector'
import * as Note from './Note'
import * as SignIn from './SignIn'
import * as Styles from './styles'
import { TosCheckbox } from './TosCheckbox'

const maxFilesSize = 3145728

export const EmbroideryLogoSetup: FC = () => {
  const { logoSetupForm, logoDisplayPrices } = LogoFormContainer.useContainer()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures },
      },
    },
  } = useTranslation()

  const { errors, textLine1, textLine2, mobileLine } = fixtures.logoUploadDropzone
  const { fileInvalidType, fileTooLarge, fileTooSmall, tooManyFiles, defaultError } = errors
  const { embroiderySiteMessages } = useEmbroidery()
  const logoLearnMoreLink = embroiderySiteMessages?.logoLearnMorelink
  const logoDigitizationWarning = embroiderySiteMessages?.logoDigitizationWarning

  const { isLoggedIn } = useAuthentication()
  const isSmallViewport = useIsViewport(ViewportSizes['small'])
  const [showNote, setShowNote] = useState(false)

  const pathname = usePathname()
  const { color } = useTheme()

  const fileValue = useStoreState(logoSetupForm, s => s.values.file)
  const notesValue = useStoreState(logoSetupForm, s => s.values.notes)
  const fileError = useStoreState(logoSetupForm, () => logoSetupForm.getError('file'))
  const digitizationSettingError = useStoreState(logoSetupForm, () =>
    logoSetupForm.getError('digitization_setting')
  )
  const isSubmitting = useStoreState(logoSetupForm, s => s.submitting)

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      logoSetupForm.submit()
    },
    [logoSetupForm]
  )

  const onFileDropped = (files: File[]) => {
    const firstFile = files[0]
    if (firstFile) {
      if (firstFile.size < maxFilesSize) {
        logoSetupForm.setValue('file', firstFile)
      } else {
        logoSetupForm.setError('file', fixtures.fileSizeError)
      }
    }
  }

  const onRemoveFile = () => {
    logoSetupForm.setValue('file', undefined)
  }

  // Re-seed the dropzone with the already-uploaded file when navigating back to LOGO_SETUP
  const preloadedFile = useMemo<PreviewFile | undefined>(() => {
    if (!fileValue) return undefined
    return Object.assign(fileValue as PreviewFile, { preview: URL.createObjectURL(fileValue) })
  }, [fileValue])

  const imgSrc = preloadedFile?.preview

  const { uploadSpecs } = fixtures

  const imageFormatsMatch = uploadSpecs.match(/PNG.*?EPS/) ?? ''

  return (
    <FormBody onSubmit={handleSubmit}>
      <Styles.Label>
        <Styles.MicroCaps>{fixtures.title}</Styles.MicroCaps>
        <PriceLabel>{logoDisplayPrices.setup}</PriceLabel>
      </Styles.Label>
      <Styles.Text>
        {!!logoDigitizationWarning ? logoDigitizationWarning + ' ' : null}
        {logoLearnMoreLink && (
          <Styles.LearnMoreLink
            target='_blank'
            href={logoLearnMoreLink}
            aria-label={fixtures.ariaLabelLearnMoreLink}
          >
            {fixtures.learnMore}
          </Styles.LearnMoreLink>
        )}
      </Styles.Text>

      <DropzoneProvider initialState={{ initialFiles: preloadedFile ? [preloadedFile] : [] }}>
        <Styles.DropzoneContainer
          errorStrings={{
            fileInvalidType,
            fileTooLarge,
            fileTooSmall,
            tooManyFiles,
            defaultError,
          }}
          onFilesAdded={onFileDropped}
          maxFileNum={1}
        >
          <DropzoneTextContainer>
            <Upload color={color.stroke.neutral.text.primary.on.background} />
            {isSmallViewport ? (
              <TypeStyle.BodyMedium>{mobileLine}</TypeStyle.BodyMedium>
            ) : (
              <>
                <TypeStyle.BodyMedium>{textLine1}</TypeStyle.BodyMedium>
                <TypeStyle.BodyMedium>{textLine2}</TypeStyle.BodyMedium>
              </>
            )}
          </DropzoneTextContainer>
          <Styles.DropzoneImagePreview onRemoveFiles={onRemoveFile} />
          <DropzoneErrors />
        </Styles.DropzoneContainer>
        <Styles.FileNames />
      </DropzoneProvider>

      {imgSrc ? (
        <>
          <ErrorMessage inset role='alert'>
            {fileError}
          </ErrorMessage>
          <Styles.StyledNameWrapper label='name-logo' title={fixtures.logoName} required>
            <Name.Input
              ariaLabelledby='name-logo'
              name='name'
              form={logoSetupForm}
              maxCharacterCount={50}
              placeholder={fixtures.logoNamePlaceholder}
            />
          </Styles.StyledNameWrapper>
          {showNote ? (
            <Note.Container>
              <Note.Header>
                <h2>{fixtures.noteLabel}</h2>
                <Note.CloseIcon onClick={() => setShowNote(false)} />
              </Note.Header>
              <Note.TextArea
                placeholder={'Placeholder'}
                maxLength={500}
                name='notes'
                value={notesValue ?? ''}
                onChange={e => logoSetupForm.setValue('notes', e.target.value)}
              />
            </Note.Container>
          ) : (
            <Note.Pill type='button' onClick={() => setShowNote(!showNote)}>
              <Note.PencilIcon />
              {fixtures.openNoteLabel}
            </Note.Pill>
          )}

          <Styles.StyledHr />
          <LogoColorSelector form={logoSetupForm} />
          <Styles.StyledHr />

          <TosCheckbox />

          <Styles.Button disabled={isSubmitting} type='submit'>
            {fixtures.nextCta}
          </Styles.Button>
          {isSmallViewport ? (
            <ErrorMessage inset role='alert'>
              {digitizationSettingError}
            </ErrorMessage>
          ) : null}
        </>
      ) : (
        <>
          <ErrorMessage inset role='alert'>
            {digitizationSettingError}
          </ErrorMessage>
          <Styles.HeadlineMicro>
            {imageFormatsMatch ? (
              <>
                {uploadSpecs.slice(0, imageFormatsMatch.index)}
                <strong>{imageFormatsMatch[0]}</strong>
                {uploadSpecs.slice((imageFormatsMatch.index ?? 0) + imageFormatsMatch[0].length)}
              </>
            ) : (
              uploadSpecs
            )}
          </Styles.HeadlineMicro>
        </>
      )}

      {!isLoggedIn && (
        <>
          <SignIn.LoginAlert icon={<></>}>
            <SignIn.LoginMessage>
              {fixtures.existingLogoLogin.ifExistingLogo}
              <SignIn.LoginLink
                href={`/account/login?redirect_url=${encodeURIComponent(pathname ?? '/')}`}
              >
                {fixtures.existingLogoLogin.loginToAccessIt}
              </SignIn.LoginLink>
              {fixtures.existingLogoLogin.existingLogoEndChar}
            </SignIn.LoginMessage>
          </SignIn.LoginAlert>
        </>
      )}
    </FormBody>
  )
}
